import React, { useState, useRef, useEffect } from 'react';
import { DeleteOutlined, CloseOutlined, InboxOutlined, EditOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import imageCompression from 'browser-image-compression';
import type { UpdateProductsRequest, FileRequest } from '../../../../store/MSCommerce/Productos/types';
import type { ProductosResponse } from '../../../../store/MSCommerce/Productos/types';
import type { CategoryResponse } from '../../../../store/MSCommerce/Categories/types';
import styles from '../Productos.module.scss';

interface UpdateProductModalProps {
  product: ProductosResponse;
  onClose: () => void;
  onSubmit: (data: UpdateProductsRequest) => void;
  onDeleteImage: (url: string) => void;
  GetAllCategorys?: () => void;
  categorias?: CategoryResponse[];
}

interface PreviewImage {
  file: File;
  preview: string;
}

const STATUS_OPTIONS = ['Disponible', 'No Disponible'];

export const UpdateProductModal: React.FC<UpdateProductModalProps> = ({
  product,
  onClose,
  onSubmit,
  onDeleteImage,
  GetAllCategorys,
  categorias,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [previews, setPreviews] = useState<PreviewImage[]>([]);
  const [dragging, setDragging] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [imagenesActuales, setImagenesActuales] = useState<string[]>(product.imagenes ?? []);
  const [form, setForm] = useState({
    nombre: product.nombre ?? '',
    price: product.price?.toString() ?? '',
    stock: product.stock?.toString() ?? '',
    status: product.status ?? 'Disponible',
    description: product.description ?? '',
    categoryName: product.category ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const field = (key: string, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleCategoryChange = (value: string) => {
    setForm(prev => ({ ...prev, categoryName: value }));
    setShowDropdown(true);
  };

  const handleSelectCategory = (nombre: string) => {
    setForm(prev => ({ ...prev, categoryName: nombre }));
    setShowDropdown(false);
  };

  const filteredCats = categorias?.filter(cat =>
    cat.nombre.toLowerCase().includes(form.categoryName.toLowerCase())
  ) ?? [];

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const newPreviews: PreviewImage[] = Array.from(files).map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeNewImage = (index: number) => {
    setPreviews(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDeleteExisting = (url: string) => {
    onDeleteImage(url);
    setImagenesActuales(prev => prev.filter(img => img !== url));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (form.price && isNaN(Number(form.price))) e.price = 'Precio válido requerido';
    if (form.stock && isNaN(Number(form.stock))) e.stock = 'Stock válido requerido';
    return e;
  };

  const compressImage = async (file: File): Promise<File> =>
    await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 1200, useWebWorker: true });

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    const imagenesNuevas: FileRequest[] = previews.length > 0
      ? await Promise.all(previews.map(async p => {
          const compressed = await compressImage(p.file);
          return { base64: await toBase64(compressed) };
        }))
      : [];

    onSubmit({
      nombre: form.nombre || null,
      price: form.price ? parseFloat(Number(form.price).toFixed(2)) : null,
      stock: form.stock ? Number(form.stock) : null,
      status: form.status || null,
      description: form.description || null,
      categoryName: form.categoryName || null,
      imagenesNuevas: imagenesNuevas.length > 0 ? imagenesNuevas : null,
    });
  };

  return (
    <div className={styles.modal__overlay} onClick={onClose}>
      <div className={styles.modal__panel} onClick={e => e.stopPropagation()}>

        <div className={styles.modal__header}>
          <div className={styles.modal__headerLeft}>
            <h2 className={styles.modal__title}>Actualizar Producto</h2>
          </div>
          <button className={styles.modal__close} onClick={onClose}><CloseOutlined /></button>
        </div>

        <div className={styles.modal__body}>

          {/* Imágenes actuales */}
          {imagenesActuales.length > 0 && (
            <div className={styles.modal__section}>
              <label className={styles.modal__label}>Imágenes actuales</label>
              <div className={styles.modal__previews}>
                {imagenesActuales.map((url, i) => (
                  <div key={i} className={styles.modal__previewItem}>
                    <img src={url} alt={`imagen-${i}`} className={styles.modal__previewImg} loading="lazy" />
                    <button
                      className={styles.modal__removeImg}
                      onClick={() => handleDeleteExisting(url)}
                      title="Eliminar imagen"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Agregar nuevas imágenes */}
          <div className={styles.modal__section}>
            <label className={styles.modal__label}>Agregar imágenes nuevas</label>
            <div
              className={`${styles.modal__dropzone} ${dragging ? styles['modal__dropzone--active'] : ''}`}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <InboxOutlined className={styles.modal__dropIcon} />
              <p className={styles.modal__dropText}>Arrastra imágenes o <span>haz clic</span></p>
              <p className={styles.modal__dropHint}>PNG, JPG, WEBP · Múltiples archivos permitidos</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)}
              />
            </div>

            {previews.length > 0 && (
              <div className={styles.modal__previews}>
                {previews.map((p, i) => (
                  <div key={i} className={styles.modal__previewItem}>
                    <img src={p.preview} alt={`new-${i}`} className={styles.modal__previewImg} />
                    <button className={styles.modal__removeImg} onClick={() => removeNewImage(i)}>
                      <DeleteOutlined />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Campos */}
          <div className={styles.modal__grid}>

            {/* Nombre */}
            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Nombre</label>
              <input
                className={styles.modal__input}
                placeholder="Nombre del producto"
                value={form.nombre}
                onChange={e => field('nombre', e.target.value)}
              />
            </div>

            {/* Status */}
            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Estado</label>
              <select
                className={styles.modal__input}
                value={form.status}
                onChange={e => field('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Categoría */}
            <div className={styles.modal__field} ref={dropdownRef}>
              <label className={styles.modal__label}>Categoría</label>
              <input
                className={styles.modal__input}
                placeholder="Busca una categoría..."
                value={form.categoryName}
                onChange={e => handleCategoryChange(e.target.value)}
                onFocus={() => {
                  if (GetAllCategorys) GetAllCategorys();
                  setShowDropdown(true);
                }}
                autoComplete="off"
              />

              {showDropdown && form.categoryName.length > 0 && filteredCats.length > 0 && (
                <div className={styles.modal__dropdown}>
                  {filteredCats.map((cat, i) => (
                    <div key={i} className={styles.modal__dropdownItem} onMouseDown={() => handleSelectCategory(cat.nombre)}>
                      <span className={styles.modal__dropdownName}>{cat.nombre}</span>
                    </div>
                  ))}
                </div>
              )}

              {showDropdown && form.categoryName.length > 0 && categorias && categorias.length > 0 && filteredCats.length === 0 && (
                <div className={styles.modal__dropdown}>
                  <div className={styles.modal__dropdownEmpty}>Sin coincidencias</div>
                </div>
              )}
            </div>

            {/* Precio */}
            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Precio</label>
              <div className={styles.modal__inputWrapper}>
                <span className={styles.modal__inputPrefix}>$</span>
                <input
                  className={`${styles.modal__input} ${styles['modal__input--prefixed']} ${errors.price ? styles['modal__input--error'] : ''}`}
                  placeholder="0.00"
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={e => field('price', e.target.value)}
                />
              </div>
              {errors.price && <span className={styles.modal__error}>{errors.price}</span>}
            </div>

            {/* Stock */}
            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Stock</label>
              <input
                className={`${styles.modal__input} ${errors.stock ? styles['modal__input--error'] : ''}`}
                placeholder="0"
                type="number"
                min="0"
                value={form.stock}
                onChange={e => field('stock', e.target.value)}
              />
              {errors.stock && <span className={styles.modal__error}>{errors.stock}</span>}
            </div>

            {/* Descripción */}
            <div className={`${styles.modal__field} ${styles['modal__field--full']}`}>
              <label className={styles.modal__label}>Descripción</label>
              <ReactQuill
                theme="snow"
                value={form.description}
                onChange={value => field('description', value)}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['clean'],
                  ],
                }}
                placeholder="Describe los beneficios del producto..."
              />
            </div>
          </div>
        </div>

        <div className={styles.modal__footer}>
          <button className={styles.modal__cancel} onClick={onClose}>Cancelar</button>
          <button className={styles.modal__submit} onClick={handleSubmit}>
            <EditOutlined /> Actualizar Producto
          </button>
        </div>

      </div>
    </div>
  );
};